/**
 * Uyyamadilu store config — the SINGLE per-store file.
 *
 * Per framework/01-framework-overview.md: launching a new store requires
 * editing THIS file + brand assets. Every other file in src/ is shared
 * framework code.
 *
 * Kiran 2026-07-05 — Moved from repo root to stores/uyyamadilu/ as part
 * of the monorepo multi-store refactor (Phase 1). The root
 * `store.config.ts` re-exports from here so nothing in src/* had to
 * change. Phase 2 introduces STORE-env resolution so `stores/<slug>/`
 * is picked dynamically.
 *
 * Fields set to `null` are pending Kiran's answer in .claude/pending-inputs.md.
 * Code reading these uses requireConfigValue() to fail fast — NEVER falls
 * back to a default. This enforces "surface the failure" per the project's
 * no-silent-fallbacks principle.
 */

import type { StoreConfig } from '../../src/models/store-config.model'

export const storeConfig: StoreConfig = {
  // Per-store slug — the SINGLE source for every Cloudflare resource name
  // (worker / queue / R2 bucket), the observability `store_id` tag, and the
  // session cookie names. `scripts/generate-configs.cjs` substitutes it into
  // the wrangler + route-seed `{{SLUG}}` placeholders; runtime code reads it
  // via src/constants/store.constants.ts. Lowercase/digits/hyphens only.
  // Kept distinct from brand.domain (the DNS name), which stays literal.
  slug: 'uyyamadilu',

  brand: {
    name: 'Uyyamadilu',
    nameScript: 'ಉಯ್ಯಮಡಿಲು',
    tagline: "A Rocking Lap, Always There",
    legalName: null, // pending P5-02
    gstin: null, // pending P5-01
    domain: 'uyyamadilu.com',
    logoPath: '/assets/logo.svg', // file pending P1-07
    faviconPath: '/assets/favicon.ico', // file pending P1-07
    ogImagePath: '/assets/og-image.jpg', // file pending P10-01
    heroImagePath: '/assets/hero.jpg', // background image — single source; swap this file to change it everywhere

    social: {
      instagram: null, // pending F-03
      whatsapp: null, // pending P3-01
      email: null // pending P2-01
    },
    contact: {
      phone: null, // pending P3-01
      email: null, // pending P2-01 — orders@uyyamadilu.com expected
      address: '007, NR Springwoods, 10th Cross, MCECHS Layout, Shivramkaranth Nagar, Bengaluru - 560077', // P4-02 confirmed 2026-05-12
      grievanceOfficer: null, // pending P11-01 — DPDP mandatory
      grievanceEmail: null // pending P11-01 — grievance@uyyamadilu.com expected
    }
  },

  // Per-layout chrome (nav / footer / drift) — Kiran 2026-07-05.
  // The homepage of a new store may not want a top nav at all; other
  // layouts may want different chrome variants. Each layout reads its
  // own key at render time; unknown values fall back to 'default'.
  chrome: {
    home:    'default',   // 'none' hides the whole top nav on the homepage
    main:    'default',
    account: 'account'
  },

  // Design tokens — the SINGLE source of truth for colors + fonts.
  // `scripts/generate-tokens.cjs` reads this block at build time and
  // writes src/ui/styles/tokens.generated.css; Tailwind theme tokens
  // then map those CSS variables across every component. Change a
  // colour here → it changes everywhere after `npm run build` (the
  // `prebuild` script regenerates the CSS automatically).
  //
  // Optional CONFIG_KV runtime override: store a JSON blob under
  // `brand:tokens` (same shape as `design`) and the web worker
  // injects it into the HTML <head> before the bundled CSS loads —
  // swap tokens without a redeploy. See SOP 13 §"Design tokens pipeline".
  design: {
    colors: {
      bgPrimary: '#F8F5EE',      // warm cream — screen background everywhere
      bgSecondary: '#F0E6D3',    // parchment — cards, AuthLayout shell
      bgDark: '#6B4A34',         // walnut — Dashboard sidebar + footer (holds light text)
      bgSurface: '#DCDCD9',      // light gray — data surfaces: tables + modal panels
      borderSurface: '#CCCCC8',  // neutral gray — table row dividers (vs warm cream border)
      primary: '#BC7E5E',        // warm clay — primary buttons (DARK text), links, brand text
      secondary: '#BC7E5E',      // unified with primary by design
      accent: '#C4622D',         // terracotta — ERROR states only
      textPrimary: '#2C2416',    // warm charcoal — body copy
      textSecondary: '#2C2416',  // dark — all copy reads dark (Kiran 2026-05-21); hierarchy via size/weight, not greys
      textLight: '#FDFAF5',      // on dark backgrounds
      success: '#6B7B3C',        // olive — success states
      border: '#E8DCC8'          // warm border
    },
    fonts: {
      // Each family also needs the matching <link> tag in index.html.
      // heading = display serif (Lora), default = body sans (Raleway),
      // kannada = the ಉಯ್ಯಮಡಿಲು wordmark face.
      heading: "'Lora', Georgia, serif",
      default: "'Raleway', system-ui, sans-serif",
      kannada: "'Noto Sans Kannada', sans-serif"
    }
  },

  products: {
    catalog: [
      {
        handle: 'uyyamadilu-bamboo-rocker',
        sku: 'MDL-ROCKER-001',
        hsnCode: '9401', // seats for babies
        gstRatePct: 12,
        price: null, // pending P1-02 — proposed ₹4,999
        compareAtPrice: null, // pending P1-02 — proposed ₹5,999
        currency: 'INR',
        minAgeMonths: 0,
        maxAgeMonths: 6,
        maxWeightKg: 9
      }
    ]
  },

  policies: {
    returns: {
      windowDays: null, // pending P6-01 — proposed 7 days
      autoApprove: null // pending P6-01
    },
    shipping: {
      freeThreshold: null, // pending — confirm before Phase 4.1
      standardRate: null, // pending — confirm before Phase 4.1
      expressRate: null // pending — may or may not offer express
    },
    cod: {
      enabled: true, // locked — Indian market essential
      surchargeAmount: null, // pending P7-01 — proposed ₹50
      otpRequired: true, // locked per SOP 16
      riskScoreThreshold: 70 // locked per SOP 16
    }
  },

  integrations: {
    razorpay: {
      keyId: null // pending P1-06 — KEY_ID is public, KEY_SECRET in CF env vars
    }
  },

  analytics: {
    ga4MeasurementId: null, // pending F-02
    posthogKey: null // pending F-02
  }
}
